/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { AnimatePresence, motion } from 'framer-motion'
import { observer } from 'mobx-react-lite'
import { FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { BoxStore } from '../../stores/RussianPoker/Box.store'
import { DepositIcon } from '../Icons'
import { Typography } from '../Typography'
import WinNotice from '../WinNotice'
import { useDelayedVisibility } from './useDelayedVisibility'

const BONUS_NOTICE_DELAY_MS = 2500
const INSURANCE_NOTICE_DELAY_MS = 1000
const BUY_DEALER_GAME_NOTICE_DELAY_MS = 300

const AnimatedNotice: FC<{ noticeKey: string; content: ReactNode }> = ({
  noticeKey,
  content,
}) => (
  <AnimatePresence mode="wait">
    {content && (
      <motion.div
        key={noticeKey}
        initial={{ opacity: 0, y: -10, scale: 0.92 }}
        animate={{ opacity: 1, y: [-10, 2, 0], scale: [0.92, 1.03, 1] }}
        exit={{ opacity: 0, y: -6, scale: 0.96 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}>
        {content}
      </motion.div>
    )}
  </AnimatePresence>
)

type DecisionBoxStatusProps = {
  box: BoxStore
  isInteractive: boolean
  isSettled: boolean
  isFinished: boolean
}

const DecisionBoxStatus: FC<DecisionBoxStatusProps> = observer(
  ({ box, isInteractive, isSettled, isFinished }) => {
    const { decision, bonus, result, insurance, buyDealerGame } = box
    const { t } = useTranslation()

    // TODO: WinNotice добавить gold вариант
    // const boxResult = box.result
    // const showBoxResult = (isSettledPhase || isFinishedPhase) && ['win', 'lose', 'tie'].includes(boxResult.outcome)

    const boxResultEligible =
      (isSettled || isFinished) &&
      ['win', 'lose', 'tie'].includes(result.outcome)
    const boxResultDelayElapsed = useDelayedVisibility(
      boxResultEligible,
      INSURANCE_NOTICE_DELAY_MS
    )
    const boxResultVisible = boxResultEligible && boxResultDelayElapsed

    const boxWin = boxResultVisible && result.outcome === 'win' && (
      <WinNotice
        title={`${t('payout')}`}
        ratio={result.combinationMultiplier}
        amount={result.combinationPayout}
        color="green"
      />
    )

    const boxLose = boxResultVisible && result.outcome === 'lose' && (
      <Typography variant="gameInfo" color="red">
        {t('boxLose')}!
      </Typography>
    )
    const boxTie = boxResultVisible && result.outcome === 'tie' && (
      <Typography variant="gameInfo" color="gold1">
        {t('tie')}!
      </Typography>
    )

    const buyDealerGameEligible =
      isSettled && buyDealerGame.hasResponded && buyDealerGame.cost > 0
    const buyDealerGameDelayElapsed = useDelayedVisibility(
      buyDealerGameEligible,
      BUY_DEALER_GAME_NOTICE_DELAY_MS
    )
    const buyDealerGameVisible =
      buyDealerGameEligible && buyDealerGameDelayElapsed
    const buyDealerGameStatus = buyDealerGameVisible && (
      <div>
        <Typography variant="gameInfo" color="gold1">
          {t('purchaseGame')}!
        </Typography>
        <Typography
          variant="win"
          color="red"
          as="div"
          css={css`
            display: flex;
            align-items: center;
            justify-content: right;
          `}>
          -{buyDealerGame.cost}&nbsp;
          <DepositIcon size="8px" color="red" />
        </Typography>
      </div>
    )

    const insuranceResult = insurance.result
    const showInsurance =
      (isSettled || isFinished) && insurance.result !== 'none'
    const insuranceDelayElapsed = useDelayedVisibility(
      showInsurance,
      INSURANCE_NOTICE_DELAY_MS
    )
    const insuranceVisible = showInsurance && insuranceDelayElapsed

    const getInsuranceProps = () => {
      if (insuranceResult === 'refund') {
        return {
          title: `${t('insuranceRefund')}`,
          color: 'gold1' as 'gold1',
          amount: undefined,
        }
      }
      const isWin = insuranceResult === 'win'
      return {
        title: isWin ? `${t('insuranceWon')}!` : `${t('insuranceLose')}!`,
        amount: isWin ? insurance.netWin : insurance.cost,
        color: isWin ? ('green' as 'green') : ('red' as 'red'),
      }
    }

    const insuranceNotice = insuranceVisible && (
      <WinNotice {...getInsuranceProps()} />
    )

    const bonusResult = bonus.result
    const bonusEligible = isInteractive && bonus.result !== 'none'
    const bonusDelayElapsed = useDelayedVisibility(
      bonusEligible,
      BONUS_NOTICE_DELAY_MS
    )
    const bonusVisible = bonusEligible && bonusDelayElapsed

    const bonusNotice = bonusVisible && (
      <WinNotice
        title={
          bonusResult === 'win' ? `${t('bonusWin')}!` : `${t('bonusLose')}!`
        }
        ratio={bonusResult === 'win' ? bonus.ratio : undefined}
        amount={bonusResult === 'win' ? bonus.netWin : bonus.amount}
        color={bonusResult === 'win' ? 'green' : 'red'}
      />
    )

    const payoutAnteEligible =
      box.buyDealerGame.hasResponded && box.buyDealerGame.cost === 0
    const payoutAnteDelayElapsed = useDelayedVisibility(
      payoutAnteEligible,
      INSURANCE_NOTICE_DELAY_MS
    )
    const payoutAnteVisible = payoutAnteEligible && payoutAnteDelayElapsed
    const payoutAnte = payoutAnteVisible && (
      <WinNotice
        title={`${t('antePayout')}!`}
        amount={box.ante}
        color="green"
      />
    )

    return (
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 3px;
          position: absolute;
          top: 3px;
          right: 3px;
          text-align: right;
        `}>
        <AnimatedNotice
          noticeKey={`box-lose-${box.seatNumber}`}
          content={boxLose}
        />
        <AnimatedNotice noticeKey={`box-tie-${box.seatNumber}`} content={boxTie} />
        <AnimatedNotice noticeKey={`box-win-${box.seatNumber}`} content={boxWin} />
        <AnimatedNotice
          noticeKey={`insurance-notice-${box.seatNumber}-${insuranceResult}`}
          content={insuranceNotice}
        />
        <AnimatedNotice
          noticeKey={`bonus-notice-${box.seatNumber}-${bonusResult}`}
          content={bonusNotice}
        />
        <AnimatedNotice
          noticeKey={`buy-dealer-${box.seatNumber}-${buyDealerGame.cost}`}
          content={buyDealerGameStatus}
        />
        <AnimatedNotice
          noticeKey={`payout-ante-${box.seatNumber}-${box.ante}`}
          content={payoutAnte}
        />
        {decision === 'pass' && (
          <Typography variant="gameInfo" color="red">
            {t('pass')}!
          </Typography>
        )}
      </div>
    )
  }
)

export default DecisionBoxStatus
